// The four launch paths from public/assets/player.js, ported to operate on a
// React-owned stage element and return a cleanup. One useEffect in GameStage
// drives this; React owns the surrounding chrome, this owns the stage DOM.
import {
  emptyNotice,
  keepGameFocused,
  loadExternalScript,
  runtimeElement,
  showStageStatus,
  waitForRuntime,
} from "./runtime";
import { loadHighscores, loadTouchControls, loadYogNet, type HighscoresApi } from "./externals";
import type { Account } from "../lib/account";
import type { GameDetail } from "../lib/types";

// CheerpJ can only be initialized once per document. The first Java game this
// page-session launches inits it; a later one forces a full reload (each Java
// game is effectively its own page load, as in the original static site).
let cheerpjStarted = false;
const CHEERPJ_START_TIMEOUT_MS = 90000;

export async function launchGame(
  stage: HTMLDivElement,
  game: GameDetail,
  account: Account | null,
): Promise<() => void> {
  const cleanups: Array<() => void> = [];
  stage.style.setProperty("--game-width", `${game.width || 640}px`);
  stage.style.setProperty("--game-aspect", `${game.width || 640} / ${game.height || 480}`);
  stage.replaceChildren();
  stage.classList.remove("port-stage");

  // Score capture must be installed BEFORE the engine launches (the sniffers
  // wrap fetch/XHR ahead of the game's score POST). Remakes/ports report directly.
  const hs = await loadHighscores().catch(() => null);
  const adapter = hs ? await hs.getAdapter(game.id).catch(() => null) : null;
  const capture = adapter && hs ? await hs.installCapture(game, adapter).catch(() => ({})) : {};

  if (game.type === "flash") {
    await launchFlash(stage, game, capture);
  } else if (game.type === "remake" || game.player?.type === "remake" || game.type === "original-applet") {
    launchRemake(stage, game, account, cleanups, hs);
  } else if (game.type === "port" || game.player?.type === "js-port") {
    await launchPort(stage, game, hs);
  } else {
    const reloading = await launchJava(stage, game, account, capture, cleanups);
    if (reloading) return () => {};
  }

  cleanups.push(keepGameFocused(stage));

  loadTouchControls()
    .then((t) => t.initTouchControls({ stage, game, runtimeElement: () => runtimeElement(stage) }))
    .catch(() => {});

  return () => {
    for (const c of cleanups) {
      try {
        c();
      } catch {
        /* best effort */
      }
    }
    stage.replaceChildren();
  };
}

function appendRetryAction(card: HTMLElement, retryText = "Retry CheerpJ"): void {
  const actions = document.createElement("p");
  actions.className = "recovery-actions";

  const retry = document.createElement("button");
  retry.type = "button";
  retry.className = "stage-button";
  retry.textContent = retryText;
  retry.addEventListener("click", () => window.location.reload());
  actions.append(retry);

  card.append(actions);
}

function stringField(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function normalizedYogRequiredAssets(game: GameDetail): string[] {
  if (!Array.isArray(game.yogRequiredAssets)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const asset of game.yogRequiredAssets) {
    const path = stringField(asset);
    if (!path || seen.has(path)) continue;
    seen.add(path);
    out.push(path);
  }
  return out;
}

function yogAssetUrl(assetPath: string, codebase?: string): string {
  if (/^https?:\/\//i.test(assetPath)) return assetPath;
  if (assetPath.startsWith("/")) return `${location.origin}${assetPath}`;
  const basePath = codebase ? (codebase.endsWith("/") ? codebase : `${codebase}/`) : "/";
  return new URL(assetPath, `${location.origin}${basePath}`).href;
}

async function yogAssetExists(assetPath: string, codebase?: string): Promise<boolean> {
  const url = yogAssetUrl(assetPath, codebase);
  try {
    const head = await fetch(url, { method: "HEAD", cache: "force-cache" });
    if (head.ok) return true;
    if (head.status !== 405 && head.status !== 501) return false;
  } catch {
    // Fall through to GET; some static/proxy handlers do not implement HEAD.
  }
  return fetch(url, { method: "GET", cache: "force-cache" })
    .then((res) => res.ok)
    .catch(() => false);
}

async function missingYogRequiredAssets(game: GameDetail, requiredAssets: string[]): Promise<string[]> {
  const missing: string[] = [];
  for (const asset of requiredAssets) {
    if (!(await yogAssetExists(asset, game.player?.codebase))) missing.push(asset);
  }
  return missing;
}

function renderYogOriginalAssetsNotice(
  stage: HTMLDivElement,
  game: GameDetail,
  details: { missingMetadata?: string[]; missingAssets?: string[] },
): void {
  const card = document.createElement("div");
  card.className = "stampbox recovery-card";
  card.setAttribute("role", "note");

  const title = document.createElement("strong");
  title.textContent = "Original Yahoo assets needed";
  card.append(title);

  const body = document.createElement("p");
  body.textContent =
    "This Yahoo applet is blocked until its recovered lobby metadata and original assets are present in the catalog.";
  card.append(body);

  const lines = [
    ...(details.missingMetadata?.length ? [`Missing metadata: ${details.missingMetadata.join(", ")}`] : []),
    ...(details.missingAssets?.length ? ["Missing assets:", ...details.missingAssets] : []),
  ];
  if (lines.length) {
    const pre = document.createElement("pre");
    pre.textContent = lines.join("\n");
    card.append(pre);
  }

  appendRetryAction(card, "Retry");
  stage.replaceChildren(card);
}

function renderCheerpjRecoveryNotice(
  stage: HTMLDivElement,
  game: GameDetail,
  details: { painted: boolean; yogOpened: boolean },
): void {
  const card = document.createElement("div");
  card.className = "stampbox recovery-card";
  card.setAttribute("role", "note");

  const title = document.createElement("strong");
  title.textContent = "CheerpJ did not finish starting";
  card.append(title);

  const body = document.createElement("p");
  if (game.family === "yog-multiplayer" && details.painted && !details.yogOpened) {
    body.textContent = "The applet drew a surface but never opened the Yahoo game socket.";
  } else if (game.family === "yog-multiplayer" && !details.painted && !details.yogOpened) {
    body.textContent = "The applet did not paint and never opened the Yahoo game socket.";
  } else {
    body.textContent = "The applet did not paint within the startup window.";
  }
  card.append(body);

  appendRetryAction(card);
  stage.replaceChildren(card);
}

function stageHasPaintedCanvas(stage: HTMLElement): boolean {
  for (const canvas of stage.querySelectorAll("canvas")) {
    const c = canvas as HTMLCanvasElement;
    if (!c.width || !c.height) continue;
    try {
      const ctx = c.getContext("2d");
      if (!ctx) continue;
      const sample = ctx.getImageData(0, 0, Math.min(c.width, 120), Math.min(c.height, 120)).data;
      const first = [sample[0], sample[1], sample[2]];
      for (let i = 4; i < sample.length; i += 4) {
        if (
          Math.abs(sample[i] - first[0]) + Math.abs(sample[i + 1] - first[1]) + Math.abs(sample[i + 2] - first[2]) >
          24
        ) {
          return true;
        }
      }
    } catch {
      return true; // canvas exists but is unreadable; assume it is drawing
    }
  }
  return false;
}

async function launchFlash(
  stage: HTMLDivElement,
  game: GameDetail,
  capture: { onFSCommand?: ((cmd: string, arg: string) => void) | null },
): Promise<void> {
  const status = showStageStatus(stage, "loading the flash player…");
  const w = window as any;
  w.RufflePlayer = w.RufflePlayer || {};
  w.RufflePlayer.config = {
    autoplay: "on",
    unmuteOverlay: "hidden",
    splashScreen: false,
    warnOnUnsupportedContent: true,
  };
  await loadExternalScript("ruffle-player", "/vendor/ruffle/ruffle.js");
  await waitForRuntime(() => w.RufflePlayer?.newest);
  const ruffle = w.RufflePlayer?.newest?.();
  if (!ruffle) {
    emptyNotice(stage, "Ruffle did not load. Check your network connection and refresh.");
    return;
  }
  const player = ruffle.createPlayer();
  player.style.width = "100%";
  player.style.height = "100%";
  player.setAttribute("aria-label", game.title);
  if (capture?.onFSCommand) {
    try {
      player.onFSCommand = capture.onFSCommand;
    } catch {
      /* older Ruffle — fscommand capture unavailable */
    }
  }
  stage.append(player);
  const api = player.ruffle ? player.ruffle() : player;
  try {
    await api.load({ url: game.player.swf });
  } catch {
    await api.load(game.player.swf);
  }
  status.remove();
}

// Multiplayer remakes/originals run in an iframe with their own socket bridge.
// Carry the reArcade identity in on the query whenever signed in (single-player
// too, so the in-game name reflects the account everywhere).
function remakeFrameUrl(game: GameDetail, account: Account | null): string {
  const url = game.player.url!;
  if (!account) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}rearcadeUser=${encodeURIComponent(account.username)}&rearcadeToken=${encodeURIComponent(account.token)}`;
}

function launchRemake(
  stage: HTMLDivElement,
  game: GameDetail,
  account: Account | null,
  cleanups: Array<() => void>,
  hs: HighscoresApi | null,
): void {
  if (!game.player?.url) {
    emptyNotice(stage, "This remake is missing its launch URL.");
    return;
  }
  const onMessage = (event: MessageEvent) => {
    const data = event.data;
    if (data && data.type === "rearcade:score" && Number.isFinite(Number(data.score))) {
      hs?.reportScore(game.id, Number(data.score));
    }
  };
  window.addEventListener("message", onMessage);
  cleanups.push(() => window.removeEventListener("message", onMessage));

  const status = showStageStatus(stage, "loading the game…");
  const frame = document.createElement("iframe");
  frame.src = remakeFrameUrl(game, account);
  frame.allow = "autoplay";
  frame.setAttribute("frameborder", "0");
  frame.title = game.title;
  frame.style.border = "0";
  frame.style.width = "100%";
  frame.style.height = "100%";
  frame.addEventListener(
    "load",
    () => {
      status.remove();
      frame.focus();
    },
    { once: true },
  );
  stage.append(frame);
  const onClick = () => frame.focus();
  stage.addEventListener("click", onClick);
  cleanups.push(() => stage.removeEventListener("click", onClick));
}

async function launchPort(stage: HTMLDivElement, game: GameDetail, hs: HighscoresApi | null): Promise<void> {
  if (!game.player?.script) {
    emptyNotice(stage, "This port is missing its launch script.");
    return;
  }
  const status = showStageStatus(stage, "loading the game…");
  const url = `${game.player.script}?v=${encodeURIComponent(String((game as any).updatedAt || "dev"))}`;
  const mod: any = await import(/* @vite-ignore */ url);
  if (typeof mod.mount !== "function") {
    emptyNotice(stage, "This port script does not expose a mount function.");
    return;
  }
  stage.classList.add("port-stage");
  await mod.mount(stage, game, { reportScore: (id: string, score: number) => hs?.reportScore(id, score) });
  status.remove();
}

async function launchJava(
  stage: HTMLDivElement,
  game: GameDetail,
  account: Account | null,
  _capture: unknown,
  cleanups: Array<() => void>,
): Promise<boolean> {
  const isYog = game.family === "yog-multiplayer";
  const yogPageId = stringField(game.yogPageId);
  const yogLdictPath = stringField(game.yogLdictPath);
  const yogRequiredAssets = normalizedYogRequiredAssets(game);
  const yogAssetsToCheck = yogLdictPath
    ? [yogLdictPath, ...yogRequiredAssets.filter((asset) => asset !== yogLdictPath)]
    : yogRequiredAssets;

  if (isYog) {
    const missingMetadata = [
      ...(!yogPageId ? ["yogPageId"] : []),
      ...(!yogLdictPath ? ["yogLdictPath"] : []),
      ...(game.yogRequiredAssets != null && !yogRequiredAssets.length ? ["yogRequiredAssets"] : []),
    ];
    if (missingMetadata.length) {
      renderYogOriginalAssetsNotice(stage, game, { missingMetadata });
      return false;
    }

    const missingAssets = await missingYogRequiredAssets(game, yogAssetsToCheck);
    if (missingAssets.length) {
      renderYogOriginalAssetsNotice(stage, game, { missingAssets });
      return false;
    }
  }

  if (cheerpjStarted) {
    window.location.reload();
    return true;
  }
  cheerpjStarted = true;
  const w = window as any;

  const preloadPromise = fetch("/assets/cheerpj-preload.json", { cache: "force-cache" })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);

  const applet = document.createElement("cheerpj-applet");
  applet.setAttribute("code", game.player.code!);
  if (game.player.archive) applet.setAttribute("archive", game.player.archive);
  applet.setAttribute("codebase", game.player.codebase!);
  applet.setAttribute("width", String(game.width || 640));
  applet.setAttribute("height", String(game.height || 480));
  applet.setAttribute("aria-label", game.title);
  if (game.player.appletName) applet.setAttribute("name", game.player.appletName);

  // Yahoo Online Games: route the applet socket to the local YOG server and inject
  // the host/port/room params plus the chrome-image CDN redirects it reads.
  const appletParams = [...(game.player.params || [])];
  if (isYog) {
    const room = game.yogRoom || "towers";
    const yogPort = game.yogPort || 11999;
    // Chrome art (logo/button/seat-icon sprite sheets) loads from these prefixes.
    // Keep them SAME-ORIGIN: CheerpJ scopes applet.getImage() to the document
    // base, so a cross-origin prefix (e.g. the :11999 relay) is blocked before it
    // can reach the yimg-proxy. The origin proxies /i,/us,/d,/images -> yimg-proxy.
    const assetBase = location.origin;
    // Catalog metadata owns original Yahoo assets. If page_id or the dictionary
    // is missing, the guard above blocks instead of launching a blank lobby.
    const pageId = yogPageId;
    appletParams.push(
      { name: "host", value: room },
      { name: "port", value: String(yogPort) },
      { name: "room", value: room },
      { name: "yport", value: `games.room.${room}` },
      { name: "logo_image_url_prefix", value: assetBase },
      { name: "ad_image_url_prefix", value: assetBase },
      { name: "ad_info_url_prefix", value: `${assetBase}/games` },
      { name: "profile_prefix", value: `${assetBase}/games` },
      { name: "intl_code", value: "us" },
      { name: "page_title", value: game.title || "reArcade" },
      { name: "agent", value: "rearcade" },
    );
    if (pageId) appletParams.push({ name: "page_id", value: pageId });
    if (yogLdictPath) {
      appletParams.push(
        { name: "ldict_url", value: yogLdictPath },
        { name: "loading_messsage", value: "Loading game text" },
      );
    }
  }
  for (const param of appletParams) {
    const node = document.createElement("param");
    node.setAttribute("name", param.name);
    node.setAttribute("value", param.value);
    applet.append(node);
  }

  stage.replaceChildren(applet);
  const status = showStageStatus(stage, "loading the java runtime…");

  await loadExternalScript("cheerpj-loader", "https://cjrtnc.leaningtech.com/4.3/loader.js", { retries: 3 });
  await waitForRuntime(() => w.cheerpjInit);
  if (!w.cheerpjInit) {
    emptyNotice(stage, "CheerpJ did not load. Check your network connection and refresh.");
    return false;
  }

  const preloadResources = await preloadPromise;
  const initOpts: any = {
    overrideDocumentBase: `${location.origin}${game.player.codebase}`,
    overrideShortcuts: (event: KeyboardEvent) =>
      event.ctrlKey && ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key),
    ...(preloadResources
      ? {
          preloadResources,
          preloadProgress: (done: number, total: number) => {
            if (status.isConnected && total) status.textContent = `loading the java runtime — ${done}/${total}`;
          },
        }
      : {}),
  };
  if (isYog) {
    const yog = await loadYogNet();
    yog.installYogNavGuard();
    // Same-origin: the unified server reverse-proxies /ws/yog to the YOG relay
    // (Vite does the same in dev). A catalog entry can still override via yogWs.
    const wsProto = location.protocol === "https:" ? "wss" : "ws";
    const wsBase = game.yogWs || `${wsProto}://${location.host}/ws/yog`;
    initOpts.natives = yog.createYogBridge({ wsBase, name: account?.username, token: account?.token });
  }
  await w.cheerpjInit(initOpts);
  status.textContent = "starting the applet…";

  // Clear the notice once the applet is visibly alive. YOG also has to open its
  // bridge socket; a painted-but-disconnected lobby still needs recovery.
  const startedAt = Date.now();
  let painted = false;
  const paintWatch = window.setInterval(() => {
    const yogOpened = !isYog || Number(w.__yog?.opened || 0) > 0;
    if (Date.now() - startedAt > CHEERPJ_START_TIMEOUT_MS) {
      window.clearInterval(paintWatch);
      renderCheerpjRecoveryNotice(stage, game, { painted, yogOpened });
      return;
    }
    painted = painted || stageHasPaintedCanvas(stage);
    if (painted && yogOpened) {
      window.clearInterval(paintWatch);
      setTimeout(() => status.remove(), 1200);
    }
  }, 400);
  cleanups.push(() => window.clearInterval(paintWatch));
  return false;
}
